(() => {
  const rootSelector = "[data-split-image-banner]";
  const version = "native-v3";

  if (window.SplitImageBanner?.version === version) {
    window.SplitImageBanner.initAll(document);
    return;
  }

  document.querySelectorAll(rootSelector).forEach((root) => {
    window.SplitImageBanner?.destroy?.(root);
    root.splitImageBannerSwiper?.destroy?.(true, true);
    delete root.splitImageBannerSwiper;
  });

  const toBoolean = (value) => value === "true";
  const toNumber = (value, fallback) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : fallback;
  };

  const updateAutoplayButton = (button, paused) => {
    if (!button) return;
    button.dataset.paused = String(paused);
    button.setAttribute("aria-pressed", String(paused));
    button.setAttribute("aria-label", paused ? "Play slideshow" : "Pause slideshow");
  };

  const findRoot = (container) => {
    if (!(container instanceof Element)) return null;
    return container.matches(rootSelector) ? container : container.querySelector(rootSelector);
  };

  const waitForImage = async (image) => {
    if (!image.complete) {
      await new Promise((resolve) => {
        image.addEventListener("load", resolve, { once: true });
        image.addEventListener("error", resolve, { once: true });
      });
    }

    if (image.decode && image.naturalWidth) {
      await image.decode().catch(() => {});
    }
  };

  const loadSlideImages = (slide) => {
    if (!slide) return Promise.resolve();
    if (slide.splitImageBannerImagePromise) return slide.splitImageBannerImagePromise;

    const templates = Array.from(slide.querySelectorAll("template[data-split-image-banner-deferred]"));
    if (!templates.length) {
      slide.dataset.imagesLoaded = "true";
      return Promise.resolve();
    }

    const images = [];
    templates.forEach((template) => {
      const fragment = template.content.cloneNode(true);
      images.push(...fragment.querySelectorAll("img"));
      template.replaceWith(fragment);
    });

    slide.splitImageBannerImagePromise = Promise.all(images.map(waitForImage)).then(() => {
      slide.dataset.imagesLoaded = "true";
    });

    return slide.splitImageBannerImagePromise;
  };

  const init = (root) => {
    if (!root || root.splitImageBannerSlider) return Boolean(root?.splitImageBannerSlider);

    const slider = root.querySelector("[data-split-image-banner-slider]");
    const wrapper = slider?.querySelector(":scope > .split-image-banner__track");
    const slides = wrapper ? Array.from(wrapper.children).filter((slide) => slide.matches(".split-image-banner__slide")) : [];
    if (!slider || !wrapper || !slides.length) return false;

    const previousButton = root.querySelector("[data-split-image-banner-prev]");
    const nextButton = root.querySelector("[data-split-image-banner-next]");
    const pagination = root.querySelector("[data-split-image-banner-pagination]");
    const autoplayButton = root.querySelector("[data-split-image-banner-autoplay-toggle]");
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const state = {
      abortController: new AbortController(),
      autoplayEnabled: toBoolean(root.dataset.autoplay) && slides.length > 1 && !prefersReducedMotion,
      autoplayDelay: toNumber(root.dataset.autoplayDelay, 5000),
      currentIndex: 0,
      direction: getComputedStyle(root).direction === "rtl" ? 1 : -1,
      editorPaused: false,
      focusPaused: false,
      hoverPaused: false,
      loopEnabled: toBoolean(root.dataset.loop) && slides.length > 1,
      navigationRequest: 0,
      resizeObserver: null,
      timer: null,
      transitionSpeed: prefersReducedMotion ? 0 : toNumber(root.dataset.transitionSpeed, 600),
      userPaused: false,
    };
    const signal = state.abortController.signal;

    root.splitImageBannerSlider = state;
    root.splitImageBannerBlockIds = slides.map((slide) => slide.dataset.slideBlockId);
    slider.setAttribute("role", "region");
    slider.setAttribute("aria-roledescription", "carousel");
    wrapper.style.transitionDuration = `${state.transitionSpeed}ms`;

    const isTemporarilyPaused = () => state.hoverPaused || state.focusPaused || state.editorPaused || document.hidden;
    const clearAutoplay = () => {
      if (state.timer) window.clearTimeout(state.timer);
      state.timer = null;
    };
    const scheduleAutoplay = () => {
      clearAutoplay();
      if (!state.autoplayEnabled || state.userPaused || isTemporarilyPaused()) return;
      if (!state.loopEnabled && state.currentIndex === slides.length - 1) return;
      state.timer = window.setTimeout(() => goTo(state.currentIndex + 1, true), state.autoplayDelay);
    };
    const updateHeight = () => {
      if (root.dataset.heightMode !== "auto") return;
      const activeHeight = slides[state.currentIndex]?.offsetHeight;
      if (activeHeight) slider.style.height = `${activeHeight}px`;
    };
    const updateControls = () => {
      slides.forEach((slide, index) => {
        const isActive = index === state.currentIndex;
        slide.setAttribute("role", "group");
        slide.setAttribute("aria-roledescription", "slide");
        slide.setAttribute("aria-label", `${index + 1} of ${slides.length}`);
        slide.setAttribute("aria-hidden", String(!isActive));
        slide.inert = !isActive;
      });

      pagination?.querySelectorAll("button").forEach((button, index) => {
        if (index === state.currentIndex) button.setAttribute("aria-current", "true");
        else button.removeAttribute("aria-current");
      });

      [[previousButton, state.currentIndex === 0], [nextButton, state.currentIndex === slides.length - 1]].forEach(([button, atEdge]) => {
        if (!button) return;
        const disabled = !state.loopEnabled && atEdge;
        button.disabled = disabled;
        button.classList.toggle("split-image-banner__arrow--disabled", disabled);
      });

      window.requestAnimationFrame(updateHeight);
    };
    const normalizeIndex = (index) => {
      if (state.loopEnabled) return (index + slides.length) % slides.length;
      return Math.max(0, Math.min(index, slides.length - 1));
    };

    const applyIndex = (index, animate = true) => {
      state.currentIndex = index;
      wrapper.style.transitionDuration = `${animate ? state.transitionSpeed : 0}ms`;
      wrapper.style.transform = `translate3d(${state.direction * state.currentIndex * 100}%, 0, 0)`;
      updateControls();
    };

    async function goTo(index, resetAutoplay = false, animate = true) {
      const targetIndex = normalizeIndex(index);
      const requestId = ++state.navigationRequest;

      await loadSlideImages(slides[targetIndex]);
      if (requestId !== state.navigationRequest) return;

      applyIndex(targetIndex, animate);
      if (resetAutoplay) scheduleAutoplay();
    }

    if (pagination) {
      const fragment = document.createDocumentFragment();
      slides.forEach((_, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "split-image-banner__pagination-button";
        button.setAttribute("aria-label", `Go to slide ${index + 1}`);
        button.addEventListener("pointerenter", () => loadSlideImages(slides[index]), { signal, passive: true });
        button.addEventListener("focus", () => loadSlideImages(slides[index]), { signal });
        button.addEventListener("click", () => void goTo(index, true), { signal });
        fragment.append(button);
      });
      pagination.replaceChildren(fragment);
    }

    previousButton?.addEventListener("pointerenter", () => loadSlideImages(slides[normalizeIndex(state.currentIndex - 1)]), { signal, passive: true });
    nextButton?.addEventListener("pointerenter", () => loadSlideImages(slides[normalizeIndex(state.currentIndex + 1)]), { signal, passive: true });
    previousButton?.addEventListener("focus", () => loadSlideImages(slides[normalizeIndex(state.currentIndex - 1)]), { signal });
    nextButton?.addEventListener("focus", () => loadSlideImages(slides[normalizeIndex(state.currentIndex + 1)]), { signal });
    previousButton?.addEventListener("click", () => void goTo(state.currentIndex - 1, true), { signal });
    nextButton?.addEventListener("click", () => void goTo(state.currentIndex + 1, true), { signal });

    if (autoplayButton && state.autoplayEnabled) {
      updateAutoplayButton(autoplayButton, false);
      autoplayButton.addEventListener("click", () => {
        state.userPaused = !state.userPaused;
        root.dataset.userPaused = String(state.userPaused);
        updateAutoplayButton(autoplayButton, state.userPaused);
        scheduleAutoplay();
      }, { signal });
    } else if (autoplayButton) {
      autoplayButton.hidden = true;
    }

    root.addEventListener("pointerenter", () => {
      state.hoverPaused = true;
      clearAutoplay();
    }, { signal });
    root.addEventListener("pointerleave", () => {
      state.hoverPaused = false;
      scheduleAutoplay();
    }, { signal });
    root.addEventListener("focusin", () => {
      state.focusPaused = true;
      clearAutoplay();
    }, { signal });
    root.addEventListener("focusout", (event) => {
      if (root.contains(event.relatedTarget)) return;
      state.focusPaused = false;
      scheduleAutoplay();
    }, { signal });
    document.addEventListener("visibilitychange", scheduleAutoplay, { signal });
    window.addEventListener("resize", updateHeight, { signal, passive: true });
    window.addEventListener("load", updateHeight, { signal, once: true });

    let pointerStartX = null;
    let suppressNextClick = false;
    slider.addEventListener("pointerdown", (event) => {
      suppressNextClick = false;
      if (event.pointerType !== "touch") return;
      pointerStartX = event.clientX;
    }, { signal });
    slider.addEventListener("pointerup", (event) => {
      if (event.pointerType !== "touch" || pointerStartX === null) return;
      const distance = event.clientX - pointerStartX;
      pointerStartX = null;
      if (Math.abs(distance) < 40) return;
      suppressNextClick = true;
      void goTo(state.currentIndex + (distance * state.direction > 0 ? 1 : -1), true);
    }, { signal });
    slider.addEventListener("pointercancel", () => {
      pointerStartX = null;
    }, { signal });
    slider.addEventListener("click", (event) => {
      if (!suppressNextClick) return;
      suppressNextClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, { capture: true, signal });

    if (window.ResizeObserver) {
      state.resizeObserver = new ResizeObserver(updateHeight);
      slides.forEach((slide) => state.resizeObserver.observe(slide));
    }

    state.goTo = goTo;
    state.scheduleAutoplay = scheduleAutoplay;
    loadSlideImages(slides[0]);
    applyIndex(0, false);
    scheduleAutoplay();
    return true;
  };

  const initAll = (container = document) => {
    const roots = [];
    if (container instanceof Element && container.matches(rootSelector)) roots.push(container);
    container.querySelectorAll?.(rootSelector).forEach((root) => roots.push(root));
    roots.forEach(init);
    return roots.length;
  };

  const destroy = (root) => {
    const state = root?.splitImageBannerSlider;
    if (!state) return;
    if (state.timer) window.clearTimeout(state.timer);
    state.abortController.abort();
    state.resizeObserver?.disconnect();
    root.querySelector("[data-split-image-banner-pagination]")?.replaceChildren();
    const slider = root.querySelector("[data-split-image-banner-slider]");
    const wrapper = slider?.querySelector(":scope > .split-image-banner__track");
    if (slider) slider.style.height = "";
    if (wrapper) {
      wrapper.style.transform = "";
      wrapper.style.transitionDuration = "";
    }
    root.querySelectorAll(".split-image-banner__slide").forEach((slide) => {
      slide.inert = false;
      slide.removeAttribute("aria-hidden");
      delete slide.splitImageBannerImagePromise;
    });
    delete root.splitImageBannerSlider;
    delete root.splitImageBannerBlockIds;
  };

  window.SplitImageBanner = { version, init, initAll, destroy };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    const root = findRoot(event.target);
    if (root) init(root);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    const root = findRoot(event.target);
    if (root) destroy(root);
  });
  document.addEventListener("shopify:block:select", (event) => {
    const slide = event.target.closest?.("[data-slide-block-id]");
    const root = slide?.closest(rootSelector);
    if (!root) return;
    init(root);
    const state = root.splitImageBannerSlider;
    const index = root.splitImageBannerBlockIds.indexOf(slide.dataset.slideBlockId);
    if (!state || index < 0) return;
    state.editorPaused = true;
    void state.goTo(index, false, false);
    if (state.timer) window.clearTimeout(state.timer);
    updateAutoplayButton(root.querySelector("[data-split-image-banner-autoplay-toggle]"), true);
  });
  document.addEventListener("shopify:block:deselect", (event) => {
    const root = event.target.closest?.(rootSelector);
    const state = root?.splitImageBannerSlider;
    if (!state) return;
    state.editorPaused = false;
    updateAutoplayButton(root.querySelector("[data-split-image-banner-autoplay-toggle]"), state.userPaused);
    state.scheduleAutoplay();
  });
})();
